import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useLocation, useNavigate} from 'react-router-dom';

import {BulkActionProgress} from 'src/components/bulk-tool';
import {BulkActionFooter} from 'src/components/bulk-tool/bulk-action-footer';
import {clearSelection} from 'src/components/data-table-selection/services/data-table-selection-slice';
import {WidthItem} from 'src/components/width-item';
import {STACKOVERFLOW_MESSAGE} from 'src/constants';
import {useBackgroundTaskManager} from 'src/contexts/background-task-context';
import {ConfirmationContext} from 'src/contexts/confirmation-context';
import {MessageContext} from 'src/contexts/message-context';
import {STATSIG_GATES} from 'src/contexts/statsig-contex/constants';
import {useEventTracking} from 'src/hooks/useEventTracking';
import {TOOL_PROGRESS_LABEL, TOOL_TASK} from 'src/modules/bulk-update/import-user';
import {
    selectImportUserTask,
    selectImportUserUuid,
    setBackgroundTask,
    setShown,
    setUuid,
} from 'src/modules/bulk-update/import-user/import-user.slice';
import {TOOL_NAME} from 'src/modules/create-user/constants';
import {getValidationError} from 'src/modules/create-user/user-bulk-import/utils';
import {
    SELECTION_KEY,
    userSearchResultAccountsActions,
    userSearchResultActions,
    userSearchResultAuditActions,
    userSearchResultCrmIdsActions,
    userSearchResultGroupsActions,
    userSearchResultLastActiveActions,
    userSearchResultNewslettersActions,
    userSearchResultSavedSearchesActions,
    userSearchResultStateActions,
} from 'src/modules/profile/account/tabs/users-and-groups/users-groups-search-result';
import {
    getUserCreationProgress,
    stopUserCreationAction,
    userBulkCreation,
} from 'src/openapi/admin/apis/BulkUserCreationApi';
import {useAppDispatch} from 'src/store/hooks';
import {getError} from 'src/utils/get-error';

import Button from '@pb/kit-component-button';
import {Dialog, DialogHeader, DialogMain} from '@pb/kit-component-dialog-v2';
import {Group} from '@pb/kit-component-group';
import {Scroll} from '@pb/kit-component-scroll';
import {useGateValue} from '@pb/kit-lib-statsig';

import {
    applyUpdatedData,
    clearUsersBulkImport,
    selectBulkUserImportTargetAccount,
    selectBulkUserImportTemplateData,
    selectBulkUserImportUpdatedData,
} from '../../services/user-bulk-import.slice';

import type {ActionOptions} from 'src/components/save-with-reason/action-with-reason-button';
import type {ValidatedBulkImportUser} from 'src/modules/create-user/user-bulk-import/typedef';

const MODAL_WIDTH = 600;

export const ValidateFileControls = () => {
    const enabled = useGateValue(STATSIG_GATES.ACCOUNT_PROVISIONING_HISTORY);
    const startingRef = useRef(false);
    const dispatch = useDispatch();
    const apiDispatch = useAppDispatch();
    const createBackgroundTask = useBackgroundTaskManager();

    const location = useLocation();
    const prevPath = location.state?.prevPath;
    const navigate = useNavigate();
    const {bulkSubmit} = useEventTracking();
    const {setErrorMessage} = useContext(MessageContext);

    const targetAccount = useSelector(selectBulkUserImportTargetAccount);
    const templateData = useSelector(selectBulkUserImportTemplateData);
    const updatedData = useSelector(selectBulkUserImportUpdatedData);

    const {removeChangedChunk, onShowModal} = useContext(ConfirmationContext);
    const [startUpdate, setStartUpdate] = useState(false);
    const [showWarningMessage, setShowWarningMessage] = useState(false);

    const cancel = useCallback(() => {
        onShowModal(() => {
            bulkSubmit({toolName: TOOL_NAME, submit: false});
            if (prevPath) {
                navigate(-1);
            } else {
                navigate('/');
            }

            dispatch(clearUsersBulkImport());
        });
    }, [prevPath, onShowModal, dispatch, navigate, bulkSubmit]);

    const back = useCallback(() => {
        onShowModal(() => dispatch(clearUsersBulkImport()));
    }, [onShowModal, dispatch]);

    const next = useCallback(() => {
        dispatch(applyUpdatedData());
        setStartUpdate(true);
    }, [dispatch]);

    const closeWarningMessage = useCallback(() => {
        if (startingRef.current) {
            return;
        }
        setShowWarningMessage(false);
        setStartUpdate(false);
    }, [setShowWarningMessage]);

    const confirm = useCallback(async (options?: ActionOptions) => {
        if (startingRef.current) {
            return 'This operation is already starting.';
        }
        setStartUpdate(false);
        bulkSubmit({toolName: TOOL_NAME, submit: true});
        if (!targetAccount) {
            return;
        }

        startingRef.current = true;
        try {
            const runImportResponse = await apiDispatch(userBulkCreation({
                ...options,
                bulkCreateUserRequest: templateData
                    .filter(user => (
                        !Object.keys(user).some(key => [
                            'email',
                            'firstName',
                            'lastName',
                            'jobTitle',
                            'city',
                            'notes',
                        ].includes(key) && !!user[key as keyof ValidatedBulkImportUser].validationError)
                    ))
                    .map(user => ({
                        email: user.email.value,
                        primaryAccountId: targetAccount.accountId,
                        firstName: user.firstName.value,
                        lastName: user.lastName.value,
                        typeCode: user.userType,
                        statusCode: user.userStatus,
                        countryCode: user.country?.code,
                        groupId: user.group?.id,
                        jobTitle: user.jobTitle.value,
                        city: user.city.value,
                        newsletterType: user.newsletters,
                        importOptions: user.importSettings,
                        notes: user.notes.value,
                    })),
            }));

            if (runImportResponse.payload.success) {
                removeChangedChunk('bulk-user-import');
                setShowWarningMessage(false);
                if (prevPath) {
                    navigate(-1);
                } else {
                    navigate('/');
                }

                const uuid = runImportResponse.payload.data;
                const backgroundTask = createBackgroundTask(
                    TOOL_TASK,
                    <BulkActionProgress
                        label={TOOL_PROGRESS_LABEL}
                        taskSelector={selectImportUserTask}
                        idSelector={selectImportUserUuid}
                        getProgress={id => apiDispatch(getUserCreationProgress({id}))}
                        stopAction={id => apiDispatch(stopUserCreationAction({id}))}
                        setShown={setShown}
                        onDone={() => {
                            dispatch(clearSelection(SELECTION_KEY));
                            dispatch(userSearchResultStateActions.setFilters({}));
                            dispatch(userSearchResultActions.requireRefresh());
                            dispatch(userSearchResultAccountsActions.requireRefresh());
                            dispatch(userSearchResultGroupsActions.requireRefresh());
                            dispatch(userSearchResultNewslettersActions.requireRefresh());
                            dispatch(userSearchResultLastActiveActions.requireRefresh());
                            dispatch(userSearchResultSavedSearchesActions.requireRefresh());
                            dispatch(userSearchResultAuditActions.requireRefresh());
                            dispatch(userSearchResultCrmIdsActions.requireRefresh());
                        }}
                    />,
                    {
                        maxStack: 1,
                        stackOverflowMessage: STACKOVERFLOW_MESSAGE,
                    },
                );

                if (backgroundTask) {
                    dispatch(setUuid(uuid));
                    dispatch(setBackgroundTask(backgroundTask));
                }
            } else {
                return getError({error: runImportResponse.payload.data, separator: ' '});
            }
        } catch {
            return 'Unable to start this operation. Please try again.';
        } finally {
            startingRef.current = false;
        }
    }, [
        prevPath,
        targetAccount,
        templateData,
        apiDispatch,
        bulkSubmit,
        createBackgroundTask,
        dispatch,
        navigate,
        removeChangedChunk,
    ]);

    const errorMessages = useMemo(() => {
        const messages: Record<number, string[]> = {};

        if (templateData) {
            templateData.forEach((templateRow, index) => {
                const error = getValidationError(templateRow);

                if (error) {
                    const existedRow = messages[index];
                    if (existedRow) {
                        messages[index] = [...existedRow, error];
                    } else {
                        messages[index] = [error];
                    }
                }
            });
        }

        return Object.keys(messages).map(index => ({
            id: +index,
            message: messages[+index].join(', '),
        }));
    }, [templateData]);


    useEffect(() => {
        if (startUpdate && !updatedData.length) {
            if (errorMessages.length || enabled) {
                setShowWarningMessage(true);
            } else {
                confirm().then(error => {
                    if (error) {
                        setErrorMessage(error);
                    }
                });
            }
        }
    }, [startUpdate, updatedData, errorMessages, confirm, enabled, setErrorMessage]);

    return (
        <Group>
            <Button variant="Type.TERTIARY" onClick={cancel}>Cancel</Button>
            <Button variant="Type.SECONDARY" onClick={back}>Back</Button>
            <Button variant="Type.PRIMARY" disabled={!templateData.length} onClick={next}>Create Users</Button>
            <Dialog
                shown={showWarningMessage}
                setShown={value => {
                    if (!value) {
                        closeWarningMessage();
                    }
                }}>
                <DialogHeader onClose={closeWarningMessage}>
                    {errorMessages.length ? 'Warning!' : 'Create Users'}
                </DialogHeader>
                <DialogMain style={{padding: 24, maxWidth: MODAL_WIDTH}}>
                    {errorMessages.length ? (
                        <Group direction="v" gap="m">
                            <WidthItem width="100%">
                                The file contains invalid records.
                                The rows with errors will not be imported to the system.
                                Do you want to continue?
                            </WidthItem>
                            <WidthItem width="100%">
                                <Scroll>
                                    <div style={{height: 290}}>
                                        <Group direction="v">
                                            {errorMessages.map(({id, message}) => (
                                                <span key={id}>Row {id + 1}: {message}</span>
                                            ))}
                                        </Group>
                                    </div>
                                </Scroll>
                            </WidthItem>
                        </Group>
                    ) : <p>Confirm the import of {templateData.length} users.</p>}
                </DialogMain>
                <BulkActionFooter action="Continue" onConfirm={confirm} disabled={templateData.length === errorMessages.length}>
                    <Button variant="Type.SECONDARY" onClick={closeWarningMessage}>Cancel</Button>
                </BulkActionFooter>
            </Dialog>
        </Group>
    );
};
